/** Dashboard super-admin (section 3.2) : 4 cartes cliquables, connexions 24/48h/7j/30j,
 * derniers événements, activité des établissements. Un seul appel GET /admin/dashboard. */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../../services/api';
import { ACTION_LABELS, TENANT_TYPE_LABELS } from '../constants';
import { timeAgo } from '../format';

function StatCard({ title, big, sub, onClick, error, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="vd-card p-5 text-left hover:bg-surface/50 transition-colors">
      <p className="kicker mb-2">{title}</p>
      <p className={`font-title text-4xl leading-none ${error ? 'adm-text-error' : ''}`}>{big}</p>
      {sub && <p className="text-xs text-n600 mt-2">{sub}</p>}
    </button>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { adminApi.dashboard().then(setD); }, []);
  if (!d) return null;

  return (
    <div data-testid="admin-dashboard" className="space-y-6 max-w-6xl">
      <h1 className="font-title text-3xl">Tableau de bord</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="card-tenants" title="Établissements" big={d.tenants.total}
          sub={`${d.tenants.active} actifs · ${d.tenants.disabled} désactivés`}
          onClick={() => navigate('/admin/tenants')} />
        <StatCard testId="card-users" title="Utilisateurs" big={d.users.total}
          sub={`${d.users.active} actifs · ${d.users.blocked} bloqués`}
          onClick={() => navigate('/admin/users')} />
        <StatCard testId="card-errors" title="Erreurs · 24 h" big={d.errors_24h} error={d.errors_24h > 0}
          sub="Voir le journal" onClick={() => navigate('/admin/logs?action=server_error')} />
        <StatCard testId="card-emails" title="Emails envoyés" big={d.emails.h24}
          sub={`${d.emails.d30} sur 30 jours`} onClick={() => navigate('/admin/logs?action=email_sent')} />
      </div>

      <div className="vd-card p-5" data-testid="dashboard-logins">
        <p className="kicker mb-3">Connexions</p>
        <div className="grid grid-cols-4 gap-4">
          {[['24 h', d.logins.h24], ['48 h', d.logins.h48], ['7 jours', d.logins.d7], ['30 jours', d.logins.d30]].map(([k, v]) => (
            <div key={k}>
              <p className="font-title text-3xl leading-none">{v}</p>
              <p className="text-xs text-n600 mt-1">{k}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-n600 mt-3">{d.logins.distinct_30d} utilisateurs distincts sur 30 jours</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="vd-card p-5" data-testid="dashboard-events">
          <div className="flex items-center justify-between mb-3">
            <p className="kicker">Derniers événements</p>
            <button className="vd-link text-xs" onClick={() => navigate('/admin/logs')}>Ouvrir le journal</button>
          </div>
          <ul className="space-y-2">
            {d.recent.map((e, i) => (
              <li key={i} className="text-sm flex items-baseline gap-2">
                <span className="text-xs text-n500 w-20 shrink-0">{timeAgo(e.date)}</span>
                <span className={`${e.action === 'server_error' ? 'adm-badge-error px-1.5' : ''}`}>
                  {ACTION_LABELS[e.action] || e.action}
                </span>
                <span className="text-n600 truncate">{e.user_email || '—'}{e.tenant_name ? ` · ${e.tenant_name}` : ''}{e.detail ? ` · ${e.detail}` : ''}</span>
              </li>
            ))}
            {!d.recent.length && <li className="text-sm text-n500">Aucun événement récent.</li>}
          </ul>
        </div>

        <div className="vd-card p-5" data-testid="dashboard-activity">
          <p className="kicker mb-3">Activité des établissements</p>
          <ul className="space-y-2">
            {d.activity.map((t) => (
              <li key={t.id} className="text-sm flex items-center justify-between gap-2 cursor-pointer hover:bg-surface/50 rounded px-2 py-1"
                onClick={() => navigate(`/admin/tenants?open=${t.id}`)}>
                <span className="min-w-0 truncate">
                  <span className="font-medium">{t.name}</span>
                  <span className="text-n600"> · {t.users} util. · {TENANT_TYPE_LABELS[t.type] || t.type}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {!t.active && <span className="adm-badge">désactivé</span>}
                  <span className="text-xs text-n500">{timeAgo(t.last_activity_at)}</span>
                </span>
              </li>
            ))}
            {!d.activity.length && <li className="text-sm text-n500">Aucun établissement.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
